import { Controller } from '@hotwired/stimulus';

// Admin-only dark / light / system theme switch (styled like the language
// dropdown).
//
// Persists the chosen mode ('system' | 'light' | 'dark') in localStorage and
// reflects the resolved theme onto <html data-theme>. 'system' follows the OS
// `prefers-color-scheme` live. The inline <head> script applies the theme
// before first paint to avoid FOUC; this controller handles runtime changes,
// the dropdown's selected-dot + label, and re-syncs on every Turbo render.
export default class extends Controller {
  static targets = ['option', 'dot', 'label'];
  static values = { key: { type: String, default: 'theme' } };

  connect() {
    this.media = window.matchMedia('(prefers-color-scheme: dark)');
    this.onSystemChange = () => {
      if (this.current === 'system') this.apply();
    };
    this.media.addEventListener('change', this.onSystemChange);

    // Keep any other theme controller instances in sync (the listener only
    // re-renders the UI, never re-applies, so there is no dispatch loop).
    this.onExternalChange = () => this.updateUI(this.current);
    window.addEventListener('theme:changed', this.onExternalChange);

    this.apply();
  }

  disconnect() {
    if (this.media) this.media.removeEventListener('change', this.onSystemChange);
    window.removeEventListener('theme:changed', this.onExternalChange);
  }

  get current() {
    return localStorage.getItem(this.keyValue) || 'dark';
  }

  set current(value) {
    localStorage.setItem(this.keyValue, value);
  }

  select(event) {
    this.current = event.params.mode;
    this.apply();
  }

  apply() {
    const mode = this.current;
    const dark = mode === 'dark' || (mode === 'system' && this.media.matches);
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    this.updateUI(mode);
    this.dispatch('changed', { detail: { mode, resolved: dark ? 'dark' : 'light' } });
  }

  updateUI(mode) {
    this.dotTargets.forEach((dot) => {
      const active = dot.dataset.mode === mode;
      dot.classList.toggle('bg-accent', active);
      dot.classList.toggle('bg-transparent', !active);
    });

    if (this.hasLabelTarget) {
      const active = this.optionTargets.find((option) => option.dataset.mode === mode);
      if (active) this.labelTarget.textContent = active.dataset.label || active.textContent.trim();
    }
  }
}
